<template>
  <div v-if="showModal" class="fixed inset-0 z-50 overflow-auto bg-smoke-light flex backdrop-blur-sm">
    <div class="relative p-8 bg-white w-full max-w-4xl m-auto flex-col flex rounded-xl shadow-2xl border border-gray-100">
      <div class="flex justify-between items-center mb-6 border-b pb-4">
        <h2 class="text-2xl font-bold text-gray-800">{{ isEdit ? 'Edit Job' : 'Create New Job' }}</h2>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <ErrorMessage v-if="showError" :message="errorMessage" :duration="5000" class="mb-4" />

      <form @submit.prevent="submitForm" class="overflow-y-auto max-h-[75vh] pr-2 custom-scrollbar">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Basic Info -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Job Title</label>
              <input v-model="form.title" type="text" placeholder="e.g. Senior Frontend Developer" class="form-input" required />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Company</label>
              <input v-model="form.company" type="text" placeholder="e.g. EdgeSpace Africa" class="form-input" required />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Location</label>
              <input v-model="form.location" type="text" placeholder="e.g. Lagos, Nigeria (Remote)" class="form-input" required />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Salary Range</label>
              <input v-model="form.salary_range" type="text" placeholder="e.g. NGN 500k - 800k / month" class="form-input" required />
            </div>
          </div>

          <!-- Specifics -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Job Type</label>
              <select v-model="form.job_type" class="form-input" required>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
                <option value="freelance">Freelance</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Experience Level</label>
              <select v-model="form.experience_level" class="form-input" required>
                <option value="entry_level">Entry Level</option>
                <option value="junior">Junior</option>
                <option value="mid_level">Mid Level</option>
                <option value="senior">Senior</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Application Link</label>
              <input v-model="form.application_link" type="url" placeholder="https://..." class="form-input" required />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Application Deadline</label>
              <input v-model="form.application_deadline" type="date" class="form-input" required />
            </div>
          </div>

          <!-- Full Width Fields -->
          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea v-model="form.description" rows="4" placeholder="Describe the role and your company culture..." class="form-input" required></textarea>
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Responsibilities (One per line)</label>
            <textarea v-model="responsibilitiesText" rows="5" placeholder="Building UI components&#10;Mentoring junior devs&#10;..." class="form-input" required></textarea>
            <p class="text-[11px] text-gray-500 mt-1 italic">Enter each responsibility on a new line. We'll automatically format them.</p>
          </div>

          <div v-if="isEdit" class="md:col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Status</label>
            <select v-model="form.status" class="form-input">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        <div class="flex justify-end gap-4 mt-8 pt-6 border-t">
          <button type="button" class="py-2.5 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors" @click="closeModal">
            Cancel
          </button>
          <button type="submit" class="py-2.5 px-8 bg-primaryDarkBg hover:bg-gray-800 text-white font-semibold rounded-lg shadow-md transition-all flex items-center gap-2" :disabled="loading">
            <span v-if="loading" class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
            {{ isEdit ? 'Update Posting' : 'Publish Job' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios'
import ErrorMessage from '../messages/ErrorMessage.vue';

export default {
  name: 'JobModal',
  components: {
    ErrorMessage,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    selectedJob: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        location: '',
        company: '',
        job_type: 'full_time',
        experience_level: 'entry_level',
        salary_range: '',
        application_link: '',
        application_deadline: '',
        status: 'published'
      },
      responsibilitiesText: '',
      showError: false,
      errorMessage: '',
      loading: false
    }
  },
  computed: {
    isEdit() {
      return !!this.selectedJob;
    }
  },
  watch: {
    selectedJob: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form = { 
            ...val,
            application_deadline: val.application_deadline ? val.application_deadline.split('T')[0] : ''
          };
          this.responsibilitiesText = val.responsibilities ? val.responsibilities.join('\n') : '';
        } else {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        title: '',
        description: '',
        location: '',
        company: '',
        job_type: 'full_time',
        experience_level: 'entry_level',
        salary_range: '',
        application_link: '',
        application_deadline: '',
        status: 'published'
      };
      this.responsibilitiesText = '';
      this.showError = false;
      this.errorMessage = '';
    },
    closeModal() {
      this.$emit('close');
      this.resetForm();
    },
    async submitForm() {
      this.showError = false;
      this.errorMessage = '';
      this.loading = true;

      // Process responsibilities
      const responsibilities = this.responsibilitiesText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const payload = {
        ...this.form,
        responsibilities
      };

      try {
        if (this.isEdit) {
            // Remove meta fields that shouldn't be patched or cause issues if unchanged
            const updatePayload = { ...payload };
            delete updatePayload.id;
            delete updatePayload.created_at;
            delete updatePayload.updated_at;

            await apiClient.patch(`/web/jobs/${this.selectedJob.id}`, updatePayload);
            this.$toast.success('Job posting updated successfully');
        } else {
            await apiClient.post('/web/jobs/', payload);
            this.$toast.success('Job posted successfully');
        }
        
        this.$emit('refresh');
        this.closeModal();
      } catch (error) {
        console.error('Error submitting job:', error);
        this.errorMessage = error.response?.data?.message || 'An error occurred while saving the job.';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.smoke-light {
  background-color: rgba(0, 0, 0, 0.4);
}

.form-input {
  @apply mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[14px] transition-all;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
</style>
